import cytoscape from 'cytoscape'

import type { Feature } from '../feature'
import type * as models from '../models'

import * as effects from './effects'
import { getElements } from './elements'
import { LAYOUT_OPTIONS } from './layout'
import * as setup from './setup'
import { getStyles } from './styles'

/**
 * Manages {@link cytoscape} instances for the given {@link Feature}
 */
export class Manager {
  /**
   * Lookup of all {@link cytoscape.Core} managed by this {@link Manager}
   */
  private readonly _instances: Partial<
    Record<models.OrgChart.Id, cytoscape.Core>
  > = {}

  constructor(private readonly _feature: Feature) {
    _feature.registerEffects(
      effects.createCytoscapeInstance(this),
      effects.destroyCytoscapeInstance(this),
      effects.clearCytoscape(this),
      effects.cleanUpCytoscape(this),
      effects.addNodeToCytoscape(this),
      effects.putNodeInCytoscape(this),
      effects.saveEditsToCytoscape(this),
      effects.deleteNodeFromCytoscape(this),
      effects.assignNodeInCytoscape(this),
      effects.undoRedoInCytoscape(this),
      effects.applyApprovedProposalToCytoscape(this),
    )
  }

  /**
   * {@link cytoscape.Core} that corresponds to the given
   * {@link models.OrgChart.Identifier}
   */
  public getInstance({
    orgChartId,
  }: models.OrgChart.Identifier): cytoscape.Core | undefined {
    return this._instances[orgChartId]
  }

  /**
   * Returns the selected {@link models.Node.id} in the {@link cytoscape.Core} for the given
   * {@link models.OrgChart.Identifier}
   */
  public getSelectedNodeId(
    identifier: models.OrgChart.Identifier,
  ): models.Node.Id | undefined {
    return this.getInstance(identifier)
      ?.nodes('node:selected')
      .map(node => node.id())[0]
  }

  /**
   * Sets up the {@link cytoscape.Core} for the given
   * {@link models.OrgChart.Identifier}
   */
  public create({ container, orgChartId }: models.Binding): void {
    const data = this._feature.selectors
      .getOrgChartData({ orgChartId })
      .asSignal()
    // Destroys the old instance, if there is one
    this.destroy({ orgChartId })
    // Creates the new instance
    const cy = (this._instances[orgChartId] = cytoscape({
      container: container(),
      elements: data && getElements(data),
      layout: LAYOUT_OPTIONS,
      style: getStyles({ orgChartId }),
    }))

    setup.setupDragAndDrop(cy, { feature: this._feature, orgChartId })
    setup.setupEditOnSelect(cy, { feature: this._feature, orgChartId })
    setup.setupNodeClasses(cy, { feature: this._feature, orgChartId })
  }

  /**
   * Tears down the {@link cytoscape.Core} for the given
   * {@link models.OrgChart.Identifier}
   */
  public resetLayout(identifier: models.OrgChart.Identifier): void {
    this.getInstance(identifier)?.layout(LAYOUT_OPTIONS).run()
  }

  /**
   * Tears down the {@link cytoscape.Core} for the given
   * {@link models.OrgChart.Identifier}
   */
  public destroy(identifier: models.OrgChart.Identifier): void {
    this.getInstance(identifier)?.destroy()
  }
}
